import React, { useState, useRef, useImperativeHandle, forwardRef } from "react";
import axios from "axios";
import { Trash2, Upload } from "lucide-react";
import ErrorModal from "../modals/error-modal/ErrorModal";
import UploadCsvModal from "../modals/upload-csv-modal/UploadCsvModal";
import { cn } from "../../lib/utils";

const CSV_INPUT_ID = "chatControlsCsvFileInput";

const ChatControls = forwardRef(function ChatControls(
  { conversationId, onChatCleared, disabled = false },
  ref
) {
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [csvFileName, setCsvFileName] = useState("");
  const [csvEmails, setCsvEmails] = useState([]);
  const [csvError, setCsvError] = useState("");
  const fileInputRef = useRef(null);

  const clearCsvSelection = () => {
    setCsvFileName("");
    setCsvEmails([]);
    setCsvError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const getCsvEmails = () => csvEmails;

  const handleConfirmClearChat = async () => {
    try {
      if (!conversationId) return;
      const base = process.env.REACT_APP_API_URL.replace(/\/+$/, "");
      const endpoint = `${base}/chat/history/${conversationId}`;
      await axios.delete(endpoint);
      onChatCleared?.();
    } catch (err) {
      console.error("Failed to clear chat history:", err);
    } finally {
      setIsClearModalOpen(false);
    }
  };

  const processCsvFile = async (file) => {
    if (!file?.name?.toLowerCase().endsWith(".csv")) {
      clearCsvSelection();
      setCsvError("Please upload a .csv file");
      setIsUploadModalOpen(true);
      return;
    }

    try {
      const base = process.env.REACT_APP_API_URL.replace(/\/+$/, "");
      const endpoint = `${base}/echomail/parse-csv`;
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const emails = res.data?.emails || [];
      if (!emails.length) {
        clearCsvSelection();
        setCsvError("No valid email addresses were found in the CSV");
        setIsUploadModalOpen(true);
        return;
      }
      setCsvError("");
      setCsvFileName(file.name);
      setCsvEmails(emails);
      if (res.data?.truncated) {
        setCsvError("Only the first 20 addresses will be used");
      }
      setIsUploadModalOpen(true);
    } catch (err) {
      console.error("CSV parse error:", err);
      clearCsvSelection();
      setCsvError(err.response?.data?.error || "Failed to parse CSV");
      setIsUploadModalOpen(true);
    }
  };

  const handleUploadClose = () => {
    if (csvEmails.length === 0) clearCsvSelection();
    setIsUploadModalOpen(false);
  };

  const handleCsvInputChange = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    await processCsvFile(file);
  };

  useImperativeHandle(ref, () => ({
    clearCsvSelection,
    processCsvFile,
    getCsvEmails,
  }));

  return (
    <>
      <ErrorModal
        open={isClearModalOpen}
        onClose={() => setIsClearModalOpen(false)}
        cancelLabel="Cancel"
        title="Clear chat?"
        message="Are you sure you want to clear your chat history?"
        primaryAction={{
          label: "Clear chat",
          onClick: handleConfirmClearChat,
        }}
      />
      <UploadCsvModal
        open={isUploadModalOpen}
        fileName={csvFileName}
        recipientCount={csvEmails.length}
        error={csvEmails.length ? null : csvError}
        warning={csvEmails.length && csvError ? csvError : null}
        onClose={handleUploadClose}
      />
      <input
        ref={fileInputRef}
        type="file"
        id={CSV_INPUT_ID}
        accept=".csv"
        className="hidden"
        onChange={handleCsvInputChange}
      />
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => document.getElementById(CSV_INPUT_ID)?.click()}
          disabled={disabled}
          className={cn(
            "flex items-center gap-1.5 text-sm transition-colors",
            "text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
          )}
        >
          <Upload className="h-4 w-4 shrink-0" />
          Upload CSV
        </button>
        <button
          type="button"
          onClick={() => setIsClearModalOpen(true)}
          disabled={disabled}
          className={cn(
            "flex items-center gap-1.5 text-sm transition-colors",
            "text-destructive hover:text-destructive/80 disabled:opacity-50 disabled:cursor-not-allowed"
          )}
        >
          <Trash2 className="h-4 w-4 shrink-0" />
          Delete Chat
        </button>
      </div>
    </>
  );
});

export default ChatControls;
